'use client';

import { Box } from '@coolblue-development/freddy';
import { AnchorBar as AnchorBarPattern } from '@coolblue-development/next-patterns/client';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import { STICKY_MAIN_CONTENT_OFFSET } from '../../../../constants';

type AnchorBarItem = {
  label: string;
  href: string;
};

type Props = {
  items: AnchorBarItem[];
  hasBackToTop?: boolean;
};

const SCROLL_OFFSET = 110;
const IN_VIEW_OPTIONS = { rootMargin: `-${SCROLL_OFFSET}px 0px -50% 0px`, threshold: 0 };

type SectionObserverProps = {
  sectionId: string;
  onVisibilityChange: (sectionId: string, inView: boolean) => void;
};

const SectionObserver = ({ sectionId, onVisibilityChange }: SectionObserverProps) => {
  const { ref, inView } = useInView(IN_VIEW_OPTIONS);

  useEffect(() => {
    const el = document.getElementById(sectionId);
    if (el) ref(el);
  }, [sectionId, ref]);

  useEffect(() => {
    onVisibilityChange(sectionId, inView);
  }, [inView, sectionId, onVisibilityChange]);

  return null;
};

const AnchorBarClient = ({ items, hasBackToTop }: Props): ReactNode => {
  const [selectedItem, setSelectedItem] = useState<string | undefined>();
  const visibleSections = useRef(new Set<string>());
  const sectionIds = items.map(item => item.href.replace('#', ''));

  // Track all visible sections to always select the topmost one in DOM order,
  // even when multiple sections are visible simultaneously.
  const handleVisibilityChange = useCallback(
    (sectionId: string, inView: boolean) => {
      if (inView) {
        visibleSections.current.add(sectionId);
      } else {
        visibleSections.current.delete(sectionId);
      }

      const topmost = sectionIds.find(id => visibleSections.current.has(id));

      if (topmost) {
        setSelectedItem(topmost);
      }
    },
    [sectionIds]
  );

  return (
    <Box
      hideUntil='lg'
      mb={500}
      sx={{ position: 'sticky', top: STICKY_MAIN_CONTENT_OFFSET, zIndex: 1 }}
      px='1rem'
      mx='-1rem'
      backgroundColor='base'
    >
      {sectionIds.map(id => (
        <SectionObserver key={id} sectionId={id} onVisibilityChange={handleVisibilityChange} />
      ))}
      <AnchorBarPattern
        items={items}
        scrollOffSet={SCROLL_OFFSET}
        selectedItem={selectedItem}
        showBackToTop={hasBackToTop}
      />
    </Box>
  );
};

export default AnchorBarClient;
